const NETWORKS = ['linkedin', 'facebook', 'x', 'instagram', 'tiktok', 'youtube'] as const;

const PERSON_FIELDS = [
	'created_on',
	'first_name',
	'last_name',
	'display_name',
	'email',
	'phone',
	'roles',
	'access_level',
	'country',
	...NETWORKS.map((network) => `is_connected_${network}`),
];

const USER_KEYS = ['key_social', 'key_office', ...PERSON_FIELDS].map((field) => `user_${field}`);

export const USER_TOUR_ACTING_AS_KEYS: readonly string[] = ['key_social', 'key_office', ...PERSON_FIELDS].map(
	(field) => `actingas_${field}`,
);

// Documentation plus the actingas_* nulling set; the normalizer does not filter on it
export const USER_TOUR_ATTRIBUTE_KEYS: readonly string[] = [
	'platform_social',
	'platform_financial',
	'platform_office',
	'platform_design',
	'platform_sites',
	'feature_design_studio',
	'plan',
	'bay_staff',
	...USER_KEYS,
	'acting_as',
	...USER_TOUR_ACTING_AS_KEYS,
	'customer_key_social',
	'customer_key_office',
	'customer_name',
];

const DATE_KEYS = new Set(['user_created_on', 'actingas_created_on']);
const ROLE_KEYS = new Set(['user_roles', 'actingas_roles']);

const isBlank = (value: unknown): boolean => value === null || (typeof value === 'string' && value.trim() === '');

const isScalar = (value: unknown): boolean =>
	value === null || ['string', 'number', 'boolean'].includes(typeof value) || value instanceof Date;

// Usertour rejects nested objects and lists of objects as "unsupported value"; null clears the attribute instead
const isUnsupported = (value: unknown): boolean =>
	Array.isArray(value) ? !value.every(isScalar) : typeof value === 'object' && value !== null && !(value instanceof Date);

const toIsoDate = (value: unknown): string | null => {
	if (isBlank(value) || !(typeof value === 'string' || typeof value === 'number' || value instanceof Date)) return null;
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const toRoles = (value: unknown): string[] | null => {
	const list = Array.isArray(value) ? value : [value];
	const roles = list.filter((role): role is string => typeof role === 'string' && role.trim() !== '');
	return roles.length ? roles : null;
};

/** Usertour types an attribute from its first value, so blanks are null (never "") and unknown stays omitted. */
export const normalizeUserTourAttributes = (raw: Record<string, unknown> | undefined): Record<string, unknown> => {
	const result: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(raw ?? {})) {
		if (value === undefined) continue;
		if (DATE_KEYS.has(key)) result[key] = toIsoDate(value);
		else if (ROLE_KEYS.has(key)) result[key] = toRoles(value);
		else result[key] = isBlank(value) || isUnsupported(value) ? null : value;
	}

	// Read the normalized flag: a blank or object-valued raw acting_as is null by now, never true
	const actingAs = result.acting_as === true;
	result.acting_as = actingAs;
	for (const key of USER_TOUR_ACTING_AS_KEYS) {
		if (!actingAs || result[key] === undefined) result[key] = null;
	}

	return result;
};
